export type CourtMode = 'full' | 'offensive' | 'defensive';
export class Court {
  scale: number;
  margin: number;
  courtWidth: number;
  courtLength: number;
  zoneWidth: number;
  zoneHeight: number;
  boardWidth: number;
  rimRadius: number;
  boardToBaseline: number;
  rimToBaseline: number;
  chargeRadius: number;
  freeThrowRadius: number;
  threePointRadius: number;
  threeToSide: number;

  constructor(scale: number = 24) {
    this.scale = scale;
    this.margin = 1.5 * scale;
    this.courtWidth = 15 * scale;
    this.courtLength = 28 * scale;
    this.zoneWidth = 4.9 * scale;
    this.zoneHeight = 5.8 * scale;
    this.boardWidth = 1.8 * scale;
    this.rimRadius = 0.375 * scale;
    this.boardToBaseline = 1.2 * scale;
    this.rimToBaseline = 1.575 * scale;
    this.chargeRadius = 1.25 * scale;
    this.freeThrowRadius = 1.8 * scale;
    this.threePointRadius = 6.75 * scale;
    this.threeToSide = 0.9 * scale;
  }

  /**
   * Dibuja media cancha. Si invert es true, dibuja la mitad invertida (defensiva).
   * Si color es true, usa colores; si es false, solo líneas negras.
   */
  public static drawHalfCourt(ctx: CanvasRenderingContext2D, invert: boolean = false, isFullCourt: boolean = false, skipBackground: boolean = false, color: boolean = false) {
    const court = new Court();
    ctx.save();
    const {
      scale,
      margin,
      courtLength,
      courtWidth,
      zoneWidth,
      zoneHeight,
      boardWidth,
      rimRadius,
      boardToBaseline,
      rimToBaseline,
      chargeRadius,
      freeThrowRadius,
      threePointRadius,
      threeToSide,
    } = court;
    const halfLength = courtLength / 2;

    if (invert) {
      if (!isFullCourt) {
        ctx.translate(0, halfLength + margin * 2);
      } else {
        ctx.translate(0, courtLength + margin * 2);
      }
      ctx.scale(1, -1);
    }

    if (!skipBackground) {
      // Si es cancha completa, el fondo/parquet cubre TODO
      if (isFullCourt) {
        ctx.fillStyle = color ? '#4287f5' : '#fff';
        ctx.fillRect(0, 0, courtWidth + margin * 2, courtLength + margin * 2);

        // Parquet SÓLO EN COLOR. Cada strip se dibuja con 1px de solape sobre
        // el siguiente (21px de ancho con paso de 20): en pantalla completa el
        // renderScale es fraccionario y el antialias de los bordes adyacentes
        // abriría costuras de 1px por las que se vería el azul del fondo.
        if (color) {
          for (let i = 0; i < courtWidth; i += 20) {
            ctx.fillStyle = i % 40 === 0 ? '#e3cfa3' : '#edd9b6';
            ctx.fillRect(i + margin, margin, 21, courtLength);
          }
        }
      } else {
        // Media cancha
        ctx.fillStyle = color ? '#4287f5' : '#fff';
        ctx.fillRect(0, 0, courtWidth + margin * 2, halfLength + margin * 2);

        // Parquet SÓLO EN COLOR. 1px de solape entre strips para evitar
        // costuras azules con renderScale fraccionario (ver comentario de
        // la cancha completa).
        if (color) {
          for (let i = 0; i < courtWidth; i += 20) {
            ctx.fillStyle = i % 40 === 0 ? '#e3cfa3' : '#edd9b6';
            ctx.fillRect(i + margin, margin, 21, halfLength);
          }
        }
      }
    }

    ctx.strokeStyle = color ? '#fff' : '#000';
    ctx.lineWidth = 2;

    // Rectángulo exterior
    ctx.strokeRect(margin, margin, courtWidth, halfLength);

    // Línea de medio campo (solo para media cancha)
    ctx.beginPath();
    ctx.moveTo(margin, margin + halfLength);
    ctx.lineTo(margin + courtWidth, margin + halfLength);
    ctx.stroke();

    // Semicírculo de tiro libre superior
    ctx.beginPath();
    ctx.arc(margin + courtWidth / 2, margin + halfLength, freeThrowRadius, 0, Math.PI, true);
    ctx.stroke();

    // Zona (rectángulo)
    if (color) {
      ctx.fillStyle = '#2196f3';
      ctx.fillRect(margin + (courtWidth - zoneWidth) / 2, margin, zoneWidth, zoneHeight);
    }
    ctx.strokeRect(margin + (courtWidth - zoneWidth) / 2, margin, zoneWidth, zoneHeight);

    // Línea superior de la zona
    ctx.beginPath();
    ctx.moveTo(margin + (courtWidth - zoneWidth) / 2, margin);
    ctx.lineTo(margin + (courtWidth + zoneWidth) / 2, margin);
    ctx.stroke();

    // Semicírculo de carga
    ctx.beginPath();
    ctx.arc(margin + courtWidth / 2, margin + boardToBaseline, chargeRadius, 0, Math.PI, false);
    ctx.stroke();

    // Semicírculo de tiro libre inferior
    ctx.beginPath();
    ctx.arc(margin + courtWidth / 2, margin + zoneHeight, freeThrowRadius, 0, Math.PI, false);
    ctx.stroke();

    // Aro
    ctx.beginPath();
    ctx.arc(margin + courtWidth / 2, margin + rimToBaseline, rimRadius, 0, 2 * Math.PI);
    ctx.stroke();

    // Tablero
    ctx.beginPath();
    ctx.moveTo(margin + courtWidth / 2 - boardWidth / 2, margin + boardToBaseline);
    ctx.lineTo(margin + courtWidth / 2 + boardWidth / 2, margin + boardToBaseline);
    ctx.stroke();

    // Triple
    const dx = courtWidth / 2 - threeToSide;
    const angle = Math.acos(dx / threePointRadius);
    ctx.beginPath();
    ctx.arc(margin + courtWidth / 2, margin + rimToBaseline, threePointRadius, angle, Math.PI - angle, false);
    ctx.stroke();

    // Laterales triple
    ctx.beginPath();
    ctx.moveTo(margin + threeToSide, margin);
    ctx.lineTo(margin + threeToSide, margin + 2.99 * scale);
    ctx.moveTo(margin + courtWidth - threeToSide, margin);
    ctx.lineTo(margin + courtWidth - threeToSide, margin + 2.99 * scale);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Centros de los aros (coordenadas del plano de la pizarra) según el modo
   * de cancha. En 'full' hay dos aros (uno por mitad); en 'offensive' y
   * 'defensive' solo uno. Las coordenadas coinciden con las que usa
   * drawHalfCourt para dibujar el aro (incluida la inversión de la mitad
   * defensiva).
   */
  public static getRimCenters(mode: CourtMode = 'full'): { x: number; y: number }[] {
    const court = new Court();
    const { margin, courtWidth, courtLength, rimToBaseline } = court;
    const halfLength = courtLength / 2;
    const x = margin + courtWidth / 2;
    // Aro de la mitad no invertida (ofensiva).
    const rimTop = { x, y: margin + rimToBaseline };
    if (mode === 'offensive') return [rimTop];
    // Aro de la mitad invertida: drawHalfCourt aplica
    // translate(0, base) + scale(1, -1), con base = media cancha o cancha
    // completa según el modo.
    if (mode === 'defensive') return [{ x, y: halfLength + margin * 2 - rimTop.y }];
    return [rimTop, { x, y: courtLength + margin * 2 - rimTop.y }];
  }

  /**
   * Dibuja la cancha según el modo: 'full', 'offensive', 'defensive'.
   * Si color es true, usa colores; si es false, solo líneas negras.
   */
  public static drawCourtMode(ctx: CanvasRenderingContext2D, mode: CourtMode = 'full', color: boolean = true) {
    ctx.clearRect(0, 0, 420, 800);
    if (mode === 'full') {
      Court.drawHalfCourt(ctx, false, true, false, color);
      Court.drawHalfCourt(ctx, true, true, true, color);
    } else if (mode === 'offensive') {
      Court.drawHalfCourt(ctx, false, false, false, color);
    } else if (mode === 'defensive') {
      Court.drawHalfCourt(ctx, true, false, false, color);
    }
  }
}
